import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    Autocomplete,
    Chip,
    Divider
} from '@mui/material';
import {
    ArrowBack,
    Save,
    Email,
    Phone,
    AccountBalance,
    CreditCard
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useEmployeesStore from '@store/EmployeesStore';
import { useAppTheme } from '@contexts/ThemeContext';
import { getAllEmployeesWithoutPagination, getEmployeeById } from '@services/HrService/EmployeesService';

// Type definitions
interface Employee {
    id: string;
    name: string;
    email: string;
    contact?: string;
    role_id?: string;
    role_name?: string;
    dept_id?: number;
    dept_name?: string;
    reporting_manager?: string | string[];
    status?: 'active' | 'inactive';
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    date_of_birth?: string;
    hire_date?: string;
    experience_years?: number;
    bank_name?: string;
    account_number?: string;
    ifsc_code?: string;
    swift_code?: string;
    pan_number?: string;
    uan_number?: string;
}

interface PaymentDetails {
    bank_name?: string;
    account_number?: string;
    ifsc_code?: string;
    swift_code?: string;
    pan_number?: string;
    uan_number?: string;
}

interface Department {
    id: number;
    dept_name: string;
}

const EmployeeEditPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { isDarkMode } = useAppTheme();
    const {
        departments,
        loading,
        fetchDepartments,
        updateEmployeeData
    } = useEmployeesStore();

    const [formData, setFormData] = useState<Partial<Employee>>({
        name: '',
        email: '',
        contact: '',
        role_id: '5bfacf62-a7f6-11f0-8c41-5eeb5bc49250',
        status: 'active',
        dept_id: undefined,
        reporting_manager: [],
        gender: undefined,
        date_of_birth: '',
        hire_date: '',
        experience_years: undefined
    });

    const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>({
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        swift_code: '',
        pan_number: '',
        uan_number: ''
    });

    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);
    const [loadingEmployee, setLoadingEmployee] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch all employees for reporting manager dropdown
    const fetchAllEmployees = async () => {
        setLoadingAllEmployees(true);
        try {
            const result = await getAllEmployeesWithoutPagination({ status: 'active' });
            if (result.success) {
                setAllEmployees(result.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch all employees:', error);
        } finally {
            setLoadingAllEmployees(false);
        }
    };

    // Fetch employee data
    const fetchEmployeeData = async () => {
        if (!id) return;

        setLoadingEmployee(true);
        try {
            const result = await getEmployeeById(id);
            if (result.success && result.data) {
                const employee = result.data;

                // Parse reporting_manager if it's a string (JSON array format)
                let reportingManagers: string[] = [];
                if (employee.reporting_manager) {
                    if (typeof employee.reporting_manager === 'string') {
                        try {
                            reportingManagers = JSON.parse(employee.reporting_manager);
                        } catch (e) {
                            reportingManagers = [employee.reporting_manager];
                        }
                    } else if (Array.isArray(employee.reporting_manager)) {
                        reportingManagers = employee.reporting_manager;
                    }
                }

                setFormData({
                    ...employee,
                    reporting_manager: reportingManagers,
                    date_of_birth: employee.date_of_birth ? employee.date_of_birth.split('T')[0] : '',
                    hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : ''
                });

                // Set payment details if they exist
                setPaymentDetails({
                    bank_name: employee.bank_name || '',
                    account_number: employee.account_number || '',
                    ifsc_code: employee.ifsc_code || '',
                    swift_code: employee.swift_code || '',
                    pan_number: employee.pan_number || '',
                    uan_number: employee.uan_number || ''
                });
            }
        } catch (error) {
            console.error('Failed to fetch employee:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load employee data',
                severity: 'error'
            });
        } finally {
            setLoadingEmployee(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchAllEmployees();
        fetchEmployeeData();
    }, [id]);

    const handleFormSubmit = async () => {
        if (!id) return;

        try {
            setSaving(true);

            // Prepare the data with reporting_manager as JSON string array
            const dataToSubmit = {
                ...formData,
                ...paymentDetails,
                reporting_manager: Array.isArray(formData.reporting_manager) && formData.reporting_manager.length > 0
                    ? JSON.stringify(formData.reporting_manager)
                    : null
            };

            await updateEmployeeData(id, dataToSubmit);
            setSnackbar({
                open: true,
                message: 'Employee updated successfully!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Form submission error:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update employee. Please try again.',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loadingEmployee) {
        return (
            <Layout>
                <Container maxWidth={false} sx={{ px: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth={false} sx={{ px: 3 }}>
                <Box sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/employees')}
                            sx={{ mb: 2 }}
                        >
                            Back to Employees
                        </Button>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Edit Employee
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            Update employee information and payment details
                        </Typography>
                    </Box>

                    {/* Form Sections in Separate Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Personal Information Section */}
                        <Card>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                    Personal Information
                                </Typography>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        InputProps={{
                                            startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Contact Number"
                                        value={formData.contact || ''}
                                        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                                        InputProps={{
                                            startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            value={formData.gender || ''}
                                            label="Gender"
                                            onChange={(e) => setFormData({ ...formData, gender: e.target.value as Employee['gender'] || undefined })}
                                        >
                                            <MenuItem value="">Not Specified</MenuItem>
                                            <MenuItem value="male">Male</MenuItem>
                                            <MenuItem value="female">Female</MenuItem>
                                            <MenuItem value="other">Other</MenuItem>
                                            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Date of Birth"
                                        type="date"
                                        value={formData.date_of_birth || ''}
                                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={formData.status || 'active'}
                                            label="Status"
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                                        >
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Employment Information Section */}
                        <Card>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                    Employment Information
                                </Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                                        <TextField
                                            fullWidth
                                            label="Role"
                                            value="Employee"
                                            disabled
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#1976d2',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel>Department</InputLabel>
                                            <Select
                                                value={formData.dept_id || ''}
                                                label="Department"
                                                onChange={(e) => setFormData({ ...formData, dept_id: e.target.value ? Number(e.target.value) : undefined })}
                                            >
                                                <MenuItem value="">None</MenuItem>
                                                {departments.map((dept: Department) => (
                                                    <MenuItem key={dept.id} value={dept.id}>
                                                        {dept.dept_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        options={allEmployees.filter((emp: Employee) => emp.id !== formData.id && emp.status === 'active')}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={
                                            Array.isArray(formData.reporting_manager)
                                                ? allEmployees.filter((emp) => formData.reporting_manager?.includes(emp.id))
                                                : []
                                        }
                                        onChange={(event, newValue) => {
                                            const managerIds = newValue.map((emp) => emp.id);
                                            setFormData({ ...formData, reporting_manager: managerIds });
                                        }}
                                        loading={loadingAllEmployees}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Reporting Managers (Optional)"
                                                placeholder="Search by name..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingAllEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {option.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                        {option.email}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    label={option.name}
                                                    {...getTagProps({ index })}
                                                    key={option.id}
                                                    size="small"
                                                />
                                            ))
                                        }
                                    />
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                                        <TextField
                                            fullWidth
                                            label="Hire Date"
                                            type="date"
                                            value={formData.hire_date || ''}
                                            onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Experience (Years)"
                                            type="number"
                                            value={formData.experience_years ?? ''}
                                            onChange={(e) => setFormData({ ...formData, experience_years: e.target.value ? parseFloat(e.target.value) : undefined })}
                                            inputProps={{ step: 0.5, min: 0 }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment & Banking Details Section */}
                        <Card>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                                    <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Payment & Banking Details
                                </Typography>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                                    <TextField
                                        fullWidth
                                        label="Bank Name"
                                        value={paymentDetails.bank_name || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, bank_name: e.target.value })}
                                        placeholder="e.g., HDFC Bank, ICICI Bank"
                                    />
                                    <TextField
                                        fullWidth
                                        label="Account Number"
                                        value={paymentDetails.account_number || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, account_number: e.target.value })}
                                        placeholder="Enter bank account number"
                                        InputProps={{
                                            startAdornment: <CreditCard sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="IFSC Code"
                                        value={paymentDetails.ifsc_code || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, ifsc_code: e.target.value.toUpperCase() })}
                                        placeholder="e.g., HDFC0001234"
                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="SWIFT Code (Optional)"
                                        value={paymentDetails.swift_code || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, swift_code: e.target.value.toUpperCase() })}
                                        placeholder="For international transfers"
                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="PAN Number"
                                        value={paymentDetails.pan_number || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, pan_number: e.target.value.toUpperCase() })}
                                        placeholder="e.g., ABCDE1234F"
                                        inputProps={{
                                            style: { textTransform: 'uppercase' },
                                            maxLength: 10
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="UAN Number"
                                        value={paymentDetails.uan_number || ''}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, uan_number: e.target.value.replace(/\D/g, '') })}
                                        placeholder="e.g., 100456789123"
                                        helperText="12-digit EPFO Universal Account Number — printed on the salary slip"
                                        inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/employees')}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                onClick={handleFormSubmit}
                                disabled={saving || !formData.name || !formData.email}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </div>

                    {/* Success/Error Snackbar */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        sx={{ top: '50% !important', transform: 'translateY(-50%)' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{
                                width: '100%',
                                backgroundColor: snackbar.severity === 'success' ? '#4caf50' : '#f44336',
                                color: 'white',
                                '& .MuiAlert-icon': {
                                    color: 'white'
                                }
                            }}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Container>
        </Layout>
    );
};

export default EmployeeEditPage;
